import { ContentDelete, PersonGroup, SocialMessage } from '@fs/zion-icon/dist/cjs/icons'
import { useHistory } from '@fs/zion-router'
import { Button, DangerButton, IconButton, ResponsiveTableRow, Row, Select, useOverlay, H5 } from '@fs/zion-ui'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { DmcResponsiveCheckboxTable } from '../../common/DmcResponsiveTable'
import useNotifications from '../../../hooks/useNotifications'
import {
  getChannelMembers,
  getChatMessage,
  getReplyChatMessages,
  getUserChatMessages,
} from '../../../services/chatService'
import { convertUTCString } from '../../../util/DateUtil'
import { ChatLink, CisLink } from '../../app/Links'
import ChannelMembersOverlay from '../../chat/common/overlays/ChannelMembersOverlay'
import RedactAllMessagesOverlay from '../../chat/common/overlays/RedactAllMessagesOverlay'
import RedactSelectedMessagesOverlay from '../../chat/common/overlays/RedactSelectedMessagesOverlay'
import RedactSingleMessageOverlay from '../../chat/common/overlays/RedactSingleMessageOverlay'
import { focusedMessageStyles } from '../../chat/common/styles'
import ChatMessage from '../../chat/common/table/ChatMessage'
import PreviousNext from '../../chat/common/table/PreviousNext'
import ReplyTable from '../../chat/common/table/ReplyTable'
import SortableColumnHeader from '../../common/SortableColumnHeader'
import { UserContext } from '../UserContext'
import { usePageTitle } from '../../app/PageTitleContext'

const USER_MESSAGES_RESULTS_PER_PAGE_PREF_KEY = 'dmc.usermessages.resultsperpage'

export default function NewUserMessages() {
  usePageView({ page_detail: 'DMConsole:UserMessages' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.messages'))
  const { error } = useNotifications()
  const redactSingleMessageOverlay = useOverlay()
  const redactSelectedMessagesOverlay = useOverlay()
  const redactAllMessagesOverlay = useOverlay()
  const { currentUser } = useContext(UserContext)
  const [resultsPerPage, setResultsPerPage] = useState(
    localStorage.getItem(USER_MESSAGES_RESULTS_PER_PAGE_PREF_KEY) || '120'
  )
  const [page, setPage] = useState(1)
  const [allSelectedMessages, setAllSelectedMessages] = useState([])
  const [selectedMessages, setSelectedMessages] = useState([])
  const [selectedMessage, setSelectedMessage] = useState()
  const [userMessagesList, setUserMessagesList] = useState()
  const [fetchingMembers, setFetchingMembers] = useState(false)
  const [fetchedMembers, setFetchedMembers] = useState(false)
  const userMessages = useMemo(
    () =>
      userMessagesList?.reduce(
        (userMessagesCombined, messages) => userMessagesCombined.concat(messages.messages),
        []
      ) || [],
    [userMessagesList]
  )
  const [sortField, setSortField] = useState()
  const [sortType, setSortType] = useState()
  const sortedUserMessages = sortUserMessages()
  const [userMessagesRepliesChannelList, setUserMessagesRepliesChannelList] = useState({})
  const [memberData, setMemberData] = useState({})
  const [expandedMessages, setExpandedMessages] = useState({})
  const [showingReplies, setShowingReplies] = useState({})
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [redactedMessagesList, setRedactedMessagesList] = useState([])
  const [refreshMessagesToggle, setRefreshMessagesToggle] = useState(false)
  const history = useHistory()
  const searchParams = new URLSearchParams(history.location.search)
  const messageId = searchParams.get('messageId')
  const resultsPerPageAsInt = parseInt(resultsPerPage, 10)
  const next = null
  const previous = null
  const userCisId = currentUser.dmcUser.cisId
  const totalReplyMessagesOnScreen = useMemo(() => {
    const userMessagesReplies = Object.values(userMessagesRepliesChannelList)?.reduce((numOfReplyMessages, channel) => {
      if (messageId === channel.id) {
        return numOfReplyMessages + channel.messages.length + 1
      }
      return numOfReplyMessages + channel.messages.length
    }, 0)
    return userMessagesReplies
  }, [messageId, userMessagesRepliesChannelList])

  const allMessagesExpanded =
    Object.keys(expandedMessages).length === userMessages.length + totalReplyMessagesOnScreen + (messageId ? 1 : 0)

  useEffect(() => {
    const getUserChatMessagesAsync = async () => {
      try {
        const response = await getUserChatMessages(userCisId, resultsPerPageAsInt + 1, page - 1, next, previous)
        const responseMessagesList = response.data.userMessagesDataDtoList || response.data
        if (response.data.notifications && response.data.notifications.length > 0) {
          const errorMessage =
            responseMessagesList.length === 0
              ? t('user.messages.errorFetching', 'Error getting user messages')
              : t('user.messages.errorFetchingBatch', 'Error getting user messages starting at: {indeces}', {
                  indeces: response.data.notifications[0].msg,
                })
          error(errorMessage)
        }
        const userMessagesResponse = responseMessagesList.map((userMessagesGroup) => {
          userMessagesGroup.messages = userMessagesGroup.messages.map((userMessage, index) => {
            userMessage.index = index + userMessagesGroup.offsetUsed
            return userMessage
          })
          return userMessagesGroup
        })
        const messagesRetrievedCount = userMessagesResponse?.reduce(
          (numOfMessages, responseMessages) => numOfMessages + responseMessages.messages.length,
          0
        )
        if (messagesRetrievedCount === resultsPerPageAsInt + 1) {
          setHasMoreMessages(true)
          userMessagesResponse[userMessagesResponse.length - 1].messages.pop()
        } else {
          setHasMoreMessages(false)
        }
        if (messageId) {
          const filteredUserMessagesList = userMessagesResponse.map((userMessagesGroup) => {
            userMessagesGroup.messages = userMessagesGroup.messages.filter((message) => message.messageId !== messageId)
            return userMessagesGroup
          })
          setUserMessagesList(filteredUserMessagesList)
        } else {
          setUserMessagesList(userMessagesResponse)
        }
      } catch (err) {
        setUserMessagesList([])
        if (!err.response.data.notifications[0].msg.includes('There are no searchable channels')) {
          error(
            `${t('user.messages.errorFetching', {
              defaultValue: 'Error getting user messages',
            })}${err.message ? `: ${err.message}` : ''}`,
            err
          )
        }
      }
      setFetchedMembers(false)
    }
    getUserChatMessagesAsync()
  }, [error, resultsPerPage, refreshMessagesToggle, t, userCisId, page, resultsPerPageAsInt, messageId])

  useEffect(() => {
    const fetchFirstBatch = async () => {
      const channelIds = [...new Set(sortedUserMessages.slice(0, 9).map((userMessage) => userMessage.cid))].filter(
        (channelId) => !Object.keys(memberData).includes(channelId)
      )
      if (channelIds.length === 0) return
      try {
        const memberDataResponse = await getChannelMembers(channelIds)
        setMemberData((prevState) => {
          return {
            ...prevState,
            ...memberDataResponse.data,
          }
        })
      } catch (err) {
        error(
          `${t('chat.errorFetchingMembers', {
            defaultValue: 'Something went wrong fetching chat members',
          })}${err.message ? `: ${err.message}` : ''}`,
          err
        )
      }
    }
    const fetchTheRest = async () => {
      const channelIds = [...new Set(sortedUserMessages.slice(9).map((userMessage) => userMessage.cid))].filter(
        (channelId) => !Object.keys(memberData).includes(channelId)
      )
      if (channelIds.length === 0) return
      try {
        const memberDataResponse = await getChannelMembers(channelIds)
        setMemberData((prevState) => {
          return {
            ...prevState,
            ...memberDataResponse.data,
          }
        })
      } catch (err) {
        error(
          `${t('chat.errorFetchingMembers', {
            defaultValue: 'Something went wrong fetching chat members',
          })}${err.message ? `: ${err.message}` : ''}`,
          err
        )
      }
    }
    const fetchMemberData = async () => {
      setFetchingMembers(true)
      await fetchFirstBatch()
      await fetchTheRest()
      setFetchedMembers(true)
      setFetchingMembers(false)
    }

    if (sortedUserMessages && sortedUserMessages.length > 0 && !fetchedMembers && !fetchingMembers) {
      fetchMemberData()
    }
  }, [error, fetchedMembers, fetchingMembers, memberData, sortedUserMessages, t])

  const onPrevious = () => {
    if (messageId) {
      searchParams.delete('messageId')
      history.replace({
        search: searchParams.toString(),
      })
    }
    setPage((prevPage) => prevPage - 1)
  }

  const onNext = () => {
    if (messageId) {
      searchParams.delete('messageId')
      history.replace({
        search: searchParams.toString(),
      })
    }
    setPage((prevPage) => prevPage + 1)
  }

  const redactSingleOnClick = (mId) => {
    setSelectedMessage(mId)
    redactSingleMessageOverlay.open()
  }

  const updateRedactedMessagesList = (newlyRedactedMessages) => {
    setRedactedMessagesList((prevState) => [...prevState, ...newlyRedactedMessages])
  }

  const refreshMessages = () => {
    setRefreshMessagesToggle((prevState) => !prevState)
  }

  const fetchReplies = async (channelId) => {
    try {
      let hasNewerReplies = false
      const response = await getReplyChatMessages(channelId, resultsPerPageAsInt + 1)
      if (response.data.messages.length === resultsPerPageAsInt + 1) {
        response.data.messages.pop()
        hasNewerReplies = true
      }
      setUserMessagesRepliesChannelList((prevState) => {
        return {
          ...prevState,
          [channelId]: {
            ...response.data,
            hasNewerReplies,
          },
        }
      })
    } catch (err) {
      error(
        `${t('chat.errorFetchingReplies', {
          defaultValue: 'Something went wrong fetching replies',
        })}${err.message ? `: ${err.message}` : ''}`,
        err
      )
    }
  }

  const onMoreLessClick = useCallback((mId) => {
    setExpandedMessages((prevState) => {
      if (prevState[mId]) {
        const expandedMessagesCopy = { ...prevState }
        delete expandedMessagesCopy[mId]
        return expandedMessagesCopy
      }
      return { ...prevState, [mId]: true }
    })
  }, [])

  const resultsPerPageOnChange = (e) => {
    setResultsPerPage(e.target.value)
    setPage(1)
    localStorage.setItem(USER_MESSAGES_RESULTS_PER_PAGE_PREF_KEY, e.target.value)
  }

  const rowOnClickOrExpand = (userMessage, isMessageInQuestion) => {
    if (!userMessagesRepliesChannelList[userMessage.replyCid]) {
      fetchReplies(userMessage.replyCid)
    }
    if (showingReplies[`${userMessage.messageId}${isMessageInQuestion ? '-messageInQuestion' : ''}`]) {
      setShowingReplies((prevState) => {
        const showingRepliesCopy = { ...prevState }
        delete showingRepliesCopy[`${userMessage.messageId}${isMessageInQuestion ? '-messageInQuestion' : ''}`]
        return showingRepliesCopy
      })
    } else {
      setShowingReplies((prevState) => {
        return { ...prevState, [`${userMessage.messageId}${isMessageInQuestion ? '-messageInQuestion' : ''}`]: true }
      })
    }
  }

  function sortUserMessages() {
    const getItem = (item, arr) => {
      if (arr.length === 0) {
        return item
      }
      if (typeof arr[0] === 'string') return getItem(item[arr[0]], arr.slice(1))
      const parentField = Object.keys(arr[0])[0]
      return getItem(item[parentField], arr[0][parentField])
    }
    const normalSort = (fields) => {
      switch (sortType) {
        case undefined: {
          return [...userMessages]
        }
        case 'ascending': {
          return [...userMessages].sort((a, b) => {
            const itemA = getItem(a, fields)
            const itemB = getItem(b, fields)
            return (itemA || '').localeCompare(itemB || '')
          })
        }
        case 'descending': {
          return [...userMessages].sort((a, b) => {
            const itemA = getItem(a, fields)
            const itemB = getItem(b, fields)
            return (itemB || '').localeCompare(itemA || '')
          })
        }
        default:
          break
      }
      return [...userMessages]
    }
    const dateSort = (field) => {
      switch (sortType) {
        case undefined: {
          return [...userMessages]
        }
        case 'ascending': {
          return [...userMessages].sort((a, b) => {
            const aDate = Date.parse(a[field])
            const bDate = Date.parse(b[field])
            if (a[field] === undefined) {
              return 1
            }
            if (b[field] === undefined) {
              return -1
            }
            if (aDate === bDate) {
              return 0
            }
            return aDate < bDate ? -1 : 1
          })
        }
        case 'descending': {
          return [...userMessages].sort((a, b) => {
            const aDate = Date.parse(a[field])
            const bDate = Date.parse(b[field])
            if (a[field] === undefined) {
              return 1
            }
            if (b[field] === undefined) {
              return -1
            }
            if (aDate === bDate) {
              return 0
            }
            return aDate < bDate ? 1 : -1
          })
        }
        default:
          break
      }
      return [...userMessages]
    }
    if (userMessages) {
      switch (sortField) {
        case 'updatedAtDate': {
          return dateSort('updatedAtDate')
        }
        case 'deletedAtDate': {
          return dateSort('deletedAtDate')
        }
        case 'channelName': {
          return normalSort([{ channelMeta: ['name'] }])
        }
        default:
          break
      }
    }
    return userMessages
  }

  const sortIconOnClick = (type, field) => {
    setSortType(type)
    setSortField(field)
  }

  const sortByFieldType = (field) => {
    if (field !== sortField) {
      sortIconOnClick('ascending', field)
    } else if (sortType === undefined) {
      sortIconOnClick('ascending', field)
    } else if (sortType === 'ascending') {
      sortIconOnClick('descending', field)
    } else if (sortType === 'descending') {
      sortIconOnClick(undefined, undefined)
    }
  }

  const appendSelectedMessages = useCallback((messagesToAdd) => {
    setAllSelectedMessages((prevState) => [...prevState, ...messagesToAdd])
  }, [])

  const removeSelectedMessages = useCallback((messagesToRemove) => {
    setAllSelectedMessages((prevState) => prevState.filter((mId) => !messagesToRemove.includes(mId)))
  }, [])

  const setSelectedMessagesAndAllMessages = useCallback(
    (messages) => {
      if (messages.length === selectedMessages.length) {
        return
      }
      if (messages.length < selectedMessages.length) {
        const messagesToRemove = selectedMessages.filter((message) => !messages.includes(message))
        removeSelectedMessages(messagesToRemove)
      } else {
        const messagesToAppend = messages.filter((message) => !selectedMessages.includes(message))
        appendSelectedMessages(messagesToAppend)
      }
      setSelectedMessages(messages)
    },
    [appendSelectedMessages, removeSelectedMessages, selectedMessages]
  )

  return (
    <>
      {sortedUserMessages && (
        <>
          {sortedUserMessages.length > 0 && (
            <>
              <Row alignY="middle">
                <Select
                  id="resultsPerPage"
                  label={t('resultsPerPage', 'Results per page')}
                  hidePlaceholder
                  options={[
                    { value: '30', label: '30' },
                    { value: '60', label: '60' },
                    { value: '90', label: '90' },
                    { value: '120', label: '120' },
                    { value: '150', label: '150' },
                  ]}
                  value={resultsPerPage}
                  onChange={resultsPerPageOnChange}
                />
                <PreviousNext
                  growX
                  direction="horizontal"
                  page={page}
                  onPrevious={onPrevious}
                  onNext={onNext}
                  disablePrevious={page === 1}
                  disableNext={!hasMoreMessages}
                />
                <Button
                  emphasis="medium"
                  Icon={SocialMessage}
                  onClick={() =>
                    setExpandedMessages(() => {
                      if (allMessagesExpanded) {
                        return {}
                      }
                      const newExpandedMessages = {}
                      sortedUserMessages.forEach((message) => {
                        newExpandedMessages[message.messageId] = true
                      })
                      Object.values(userMessagesRepliesChannelList).forEach((channel) => {
                        channel.messages.forEach((message) => {
                          if (messageId === channel.id) {
                            newExpandedMessages[`${message.messageId}-messageInQuestion`] = true
                          }
                          newExpandedMessages[message.messageId] = true
                        })
                      })
                      if (messageId) {
                        newExpandedMessages[`${messageId}-messageInQuestion`] = true
                      }
                      return newExpandedMessages
                    })
                  }
                >
                  {allMessagesExpanded ? t('collapseAll', 'Collapse All') : t('expandAll', 'Expand All')}
                </Button>
                {allSelectedMessages.length > 0 ? (
                  <>
                    <DangerButton onClick={redactSelectedMessagesOverlay.handleClick}>
                      {t('redactSelected', 'Redact Selected')}
                    </DangerButton>
                    <RedactSelectedMessagesOverlay
                      selectedMessages={allSelectedMessages}
                      updateRedactedMessagesList={updateRedactedMessagesList}
                      {...redactSelectedMessagesOverlay}
                    />
                  </>
                ) : (
                  <>
                    <DangerButton onClick={redactAllMessagesOverlay.handleClick}>
                      {t('redactAll', 'Redact All')}
                    </DangerButton>
                    <RedactAllMessagesOverlay
                      cisId={userCisId}
                      refreshMessages={refreshMessages}
                      {...redactAllMessagesOverlay}
                    />
                  </>
                )}
              </Row>
              <RedactSingleMessageOverlay
                selectedMessage={selectedMessage}
                updateRedactedMessagesList={updateRedactedMessagesList}
                {...redactSingleMessageOverlay}
              />
              <MessageInQuestion
                messageId={messageId}
                rowOnClickOrExpand={rowOnClickOrExpand}
                showingReplies={showingReplies}
                expandedMessages={expandedMessages}
                onMoreLessClick={onMoreLessClick}
                memberData={memberData}
                setMemberData={setMemberData}
                redactSingleOnClick={redactSingleOnClick}
                userMessagesRepliesChannelList={userMessagesRepliesChannelList}
                appendSelectedMessages={appendSelectedMessages}
                removeSelectedMessages={removeSelectedMessages}
                redactedMessagesList={redactedMessagesList}
              />
              <DmcResponsiveCheckboxTable
                id="User Message Table"
                totalColumnCount={6}
                onSelectionChange={setSelectedMessagesAndAllMessages}
                atSizeColumns={{
                  default: {
                    labels: [
                      '#',
                      t('message', 'Message'),
                      t('id', 'ID'),
                      <SortableColumnHeader
                        sortField={sortField}
                        sortType={sortType}
                        field="channelName"
                        onClick={() => sortByFieldType('channelName')}
                        label={t('channel', 'Channel')}
                        block
                      />,
                      <SortableColumnHeader
                        sortField={sortField}
                        sortType={sortType}
                        field="updatedAtDate"
                        onClick={() => sortByFieldType('updatedAtDate')}
                        label={t('updated', 'Updated')}
                        block
                      />,
                      <SortableColumnHeader
                        sortField={sortField}
                        sortType={sortType}
                        field="deletedAtDate"
                        onClick={() => sortByFieldType('deletedAtDate')}
                        label={`${t('redacted', 'Redacted')}/${t('deleted', 'Deleted')}`}
                        block
                      />,
                    ],
                    columnWidths: [1, 3, 2, 2, 2, 2],
                  },
                }}
              >
                {sortedUserMessages.map((userMessage) => {
                  return (
                    <ResponsiveTableRow
                      key={userMessage.messageId}
                      onClick={userMessage.replyCount ? () => rowOnClickOrExpand(userMessage) : undefined}
                      onExpandToggle={userMessage.replyCount ? () => rowOnClickOrExpand(userMessage) : undefined}
                      selected={showingReplies[userMessage.messageId]}
                      name={userMessage.messageId}
                      disabled={
                        userMessage.deletedAtDate || redactedMessagesList.includes(userMessage.messageId)
                          ? {
                              Icon: ContentDelete,
                            }
                          : undefined
                      }
                      column1={userMessage.index + 1}
                      column2={
                        <ChatMessage
                          message={userMessage}
                          showMore={expandedMessages[userMessage.messageId]}
                          onMoreLessClick={() => onMoreLessClick(userMessage.messageId)}
                        />
                      }
                      column3={
                        <span style={{ wordWrap: 'break-word' }}>
                          <ChatLink
                            channelId={userMessage.cid}
                            text={userMessage.messageId}
                            messageId={userMessage.messageId}
                          />
                        </span>
                      }
                      column4={
                        <ChannelName userMessage={userMessage} memberData={memberData} setMemberData={setMemberData} />
                      }
                      column5={userMessage.updatedAtDate && convertUTCString(userMessage.updatedAtDate, true)}
                      column6={
                        userMessage.deletedAtDate ? (
                          convertUTCString(userMessage.deletedAtDate, true)
                        ) : (
                          <IconButton
                            data-testid="delete message button"
                            Icon={ContentDelete}
                            onClick={() => redactSingleOnClick(userMessage.messageId)}
                            color="red"
                          />
                        )
                      }
                    >
                      {userMessage.replyCid && (
                        <>
                          {userMessagesRepliesChannelList[userMessage.replyCid] && (
                            <ReplyTable
                              replyChannel={userMessagesRepliesChannelList[userMessage.replyCid]}
                              expandedMessages={expandedMessages}
                              onMoreLessClick={onMoreLessClick}
                              redactSingleOnClick={redactSingleOnClick}
                              appendSelectedMessages={appendSelectedMessages}
                              removeSelectedMessages={removeSelectedMessages}
                              setChannelMessagesReplyThreads={setUserMessagesRepliesChannelList}
                              redactedMessagesList={redactedMessagesList}
                              resultsPerPage={resultsPerPageAsInt}
                            />
                          )}
                        </>
                      )}
                    </ResponsiveTableRow>
                  )
                })}
              </DmcResponsiveCheckboxTable>
            </>
          )}
          {userMessagesList?.length === 0 && (
            <H5 supplementary>{t('user.messages.noMessages', 'No messages found')}</H5>
          )}
        </>
      )}
    </>
  )
}

function ChannelName({ userMessage, memberData }) {
  const { t } = useTranslation()
  const channelMembersOverlay = useOverlay()
  const channelNameRef = useRef()

  const channelMembersIndicator = () => {
    let channelMembersIndicatorComponent = null
    let channelMembers
    if (memberData && memberData[userMessage.cid]) {
      channelMembers = Object.entries(memberData[userMessage.cid])
      if (channelMembers.length === 0) {
        channelMembersIndicatorComponent = `0 ${t('members', 'Members').toLowerCase()}`
      } else if (channelMembers.length === 1) {
        channelMembersIndicatorComponent = <CisLink text={channelMembers[0][1]} userId={channelMembers[0][0]} />
      } else if (channelMembers.length === 2) {
        channelMembersIndicatorComponent = (
          <span>
            <CisLink text={channelMembers[0][1]} userId={channelMembers[0][0]} /> {t('and', 'And').toLowerCase()}{' '}
            <CisLink text={channelMembers[1][1]} userId={channelMembers[1][0]} />
          </span>
        )
      } else {
        channelMembersIndicatorComponent = (
          <span>
            <CisLink text={channelMembers[0][1]} userId={channelMembers[0][0]} />,{' '}
            <CisLink text={channelMembers[1][1]} userId={channelMembers[1][0]} />, +
            {userMessage.channelMeta.memberCount - 2}
          </span>
        )
      }
    }
    return channelMembersIndicatorComponent
  }

  return (
    <>
      <span style={{ wordWrap: 'break-word' }}>
        <ChatLink
          channelId={userMessage.cid}
          text={
            userMessage.channelMeta.name ||
            `${userMessage.channelMeta.id.substring(0, 15)}...${userMessage.channelMeta.id.substring(
              userMessage.channelMeta.id.length - 5
            )}`
          }
        />
      </span>
      {memberData[userMessage.cid] && (
        <>
          <Row gutters="nano" alignY="middle">
            {channelMembersIndicator()}
            <IconButton
              id="Channel Members Button"
              data-testid="channel members icon button"
              Icon={PersonGroup}
              onClick={() => channelMembersOverlay.open({ target: channelNameRef.current })}
              ref={channelNameRef}
            />
          </Row>
          <ChannelMembersOverlay channelId={userMessage.cid} memberData={memberData} {...channelMembersOverlay} />
        </>
      )}
    </>
  )
}

function MessageInQuestion({
  messageId,
  rowOnClickOrExpand,
  showingReplies,
  expandedMessages,
  onMoreLessClick,
  memberData,
  setMemberData,
  redactSingleOnClick,
  userMessagesRepliesChannelList,
  appendSelectedMessages,
  removeSelectedMessages,
  redactedMessagesList,
}) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [messageInQuestion, setMessageInQuestion] = useState()
  const [selectedMessages, setSelectedMessages] = useState([])
  const replyChannel = messageInQuestion && userMessagesRepliesChannelList[messageInQuestion.replyCid]

  const setSelectedMessagesAndAllMessages = useCallback(
    (messages) => {
      if (messages.length === selectedMessages.length) {
        return
      }
      if (messages.length < selectedMessages.length) {
        removeSelectedMessages([`${messageInQuestion.messageId}-messageInQuestion`])
      } else {
        appendSelectedMessages(messages)
      }
      setSelectedMessages(messages)
    },
    [appendSelectedMessages, messageInQuestion?.messageId, removeSelectedMessages, selectedMessages]
  )

  useEffect(() => {
    const fetchMessage = async () => {
      try {
        const messageResponse = await getChatMessage(messageId)
        setMessageInQuestion(messageResponse.data)
      } catch (err) {
        error(
          `${t('chat.errorFetchingMessage', {
            defaultValue: 'Something went wrong fetching message',
          })}${err.message ? `: ${err.message}` : ''}`,
          err
        )
      }
    }
    if (messageId) {
      fetchMessage()
    }
  }, [error, messageId, t])

  if (!messageId || !messageInQuestion) {
    return null
  }
  return (
    <>
      <H5>{t('chat.selectedMessage', 'Selected Message')}</H5>
      <DmcResponsiveCheckboxTable
        totalColumnCount={5}
        onSelectionChange={setSelectedMessagesAndAllMessages}
        atSizeColumns={{
          default: {
            labels: [
              t('message', 'Message'),
              t('id', 'ID'),
              t('channel', 'Channel'),
              t('updated', 'Updated'),
              `${t('redacted', 'Redacted')}/${t('deleted', 'Deleted')}`,
            ],
            columnWidths: [4, 2, 2, 2, 2],
          },
        }}
      >
        <ResponsiveTableRow
          onClick={messageInQuestion.replyCount ? () => rowOnClickOrExpand(messageInQuestion, true) : undefined}
          onExpandToggle={messageInQuestion.replyCount ? () => rowOnClickOrExpand(messageInQuestion, true) : undefined}
          selected={showingReplies[`${messageInQuestion.messageId}-messageInQuestion`]}
          name={`${messageInQuestion.messageId}-messageInQuestion`}
          disabled={
            messageInQuestion.deletedAtDate || redactedMessagesList.includes(messageInQuestion.messageId)
              ? {
                  Icon: ContentDelete,
                }
              : undefined
          }
          column1={
            <ChatMessage
              message={messageInQuestion}
              showMore={expandedMessages[`${messageInQuestion.messageId}-messageInQuestion`]}
              onMoreLessClick={() => onMoreLessClick(`${messageInQuestion.messageId}-messageInQuestion`)}
            />
          }
          column2={
            <div css={focusedMessageStyles}>
              <ChatLink
                channelId={messageInQuestion.cid}
                text={messageInQuestion.messageId}
                messageId={messageInQuestion.messageId}
              />
            </div>
          }
          column3={
            <ChannelName userMessage={messageInQuestion} memberData={memberData} setMemberData={setMemberData} />
          }
          column4={messageInQuestion.updatedAtDate && convertUTCString(messageInQuestion.updatedAtDate, true)}
          column5={
            messageInQuestion.deletedAtDate ? (
              convertUTCString(messageInQuestion.deletedAtDate, true)
            ) : (
              <IconButton
                data-testid="delete message button"
                Icon={ContentDelete}
                onClick={() => redactSingleOnClick(messageInQuestion.messageId)}
                color="red"
              />
            )
          }
        >
          {messageInQuestion.replyCid && (
            <>
              {replyChannel && (
                <ReplyTable
                  replyChannel={replyChannel}
                  expandedMessages={expandedMessages}
                  onMoreLessClick={onMoreLessClick}
                  redactSingleOnClick={redactSingleOnClick}
                  appendSelectedMessages={appendSelectedMessages}
                  removeSelectedMessages={removeSelectedMessages}
                  redactedMessagesList={redactedMessagesList}
                  isMessageInQuestion
                />
              )}
            </>
          )}
        </ResponsiveTableRow>
      </DmcResponsiveCheckboxTable>
      <H5>{t('user.messages', 'User Messages')}</H5>
    </>
  )
}
